import React, { useCallback } from "react";
import { compose } from "recompose";
import { connect } from "react-redux";
import { getColorForPin, getIQRBounds } from "components/explore/gmap/helpers";
import _ from "lodash";
import { appNotify } from "store/actions";
import { fields } from "../resultsGrid/gridHelpers/fields";
import PinWithTooltip from 'components/explore/gmap/PinWithTooltip';
import { formatValue } from "utilities/format";
import { colorPalettePresets } from 'components/explore/gmap/colorHelper';

const propertiesCountFieldObj = fields.find(f=>f.dataField === 'propertiesCount');

const getColorMatrixObj = (pins, pinDisplayType) => {
  if (!pins) return {};
  const colorByField = pinDisplayType?.dataField ?? 'propertiesCount';
  return {
    maxVal: _.maxBy(pins, colorByField)[colorByField],
    minVal: _.minBy(pins, colorByField)[colorByField],
    noDataColor: '#afafaf'
  }
}
const roundToNearestMultiple = (input, roundTo) => {
  if (!roundTo) return input;
  const rounded = Math.round(input / roundTo) * roundTo;
  return rounded;
}

const roundToNextMultiple = (input, roundTo) => {
  if (!roundTo) return input;
  const rounded = Math.ceil(input / roundTo) * roundTo;
  // console.log(`Round ${input} to next multiple of ${roundTo}, result: ${rounded} `);
  return rounded;
}

function PropertyDataPins({ objectId, isHighlighted, pinDisplayType, pinColorIds, pinColorPaletteId, propertyData, onPinClick,
  enableRounding, isAggregateData, appNotify }) { 
  const handlePinClick = useCallback((e, id) => {
    onPinClick(e, id, objectId);
  }, [objectId, onPinClick]);
  
  if (!propertyData.length) return null;

  const colorMatrixObj = getColorMatrixObj(propertyData);
  
  if (!pinDisplayType || pinDisplayType.dataType !== 'number') { // For non-numeric or null pinDisplayType
    return propertyData.map((pin, i) => {
      const propCountFormatted = formatValue(pin.propertiesCount, propertiesCountFieldObj.format, true, '-n/a-');
      const value = pinDisplayType ? pin[pinDisplayType.dataField] : pin.propertiesCount;
      const tooltip = pinDisplayType && !pin.isProperty ? `${propCountFormatted} | ${pin.clusterName}` : pin.clusterName;
      const text = pinDisplayType ? formatValue(value, pinDisplayType.format, true, '-n/a-') : propCountFormatted;
      const palette = colorPalettePresets.find(palette => palette.id === pinColorPaletteId)
      const colorMatrix = getColorForPin(palette, value, colorMatrixObj.minVal, colorMatrixObj.maxVal, colorMatrixObj.noDataColor)
      
      return <PinWithTooltip
        key={objectId + pin.clusterID}
        id={pin.clusterID}
        objectId={objectId}
        onClick={handlePinClick}
        isProperty={pin.isProperty}
        hasPinDisplayType={pinDisplayType != null}
        pinDisplayType={pinDisplayType}
        hasNonNumericPinDisplayType={pinDisplayType?.dataType !== 'number'}
        pinColorIds={pinColorIds}
        lat={pin.latitude}
        lng={pin.longitude}
        tooltip={tooltip}
        value={value}
        text={text}
        valueColor={colorMatrix.valueColor}
        isHighlighted={isHighlighted}
      />;
    })
  } else {
    let roundingInterval;
    const targetValueCount = 30;
    const minPinCount = 500;
    // console.log('-------------------Rounding Logs----------------------------------------------------------------------------')
    const filteredTotalData = propertyData.filter(pin => pin[pinDisplayType.dataField] != null).map(el => el[pinDisplayType.dataField])
    const filteredPropertyData = propertyData.filter(pin => pin[pinDisplayType.dataField] != null && pin.isProperty).map(el => el[pinDisplayType.dataField]);
    const { lower, upper } = getIQRBounds(filteredPropertyData); 
    const range = upper - lower; // Calculate the range of the remaining values
    // console.log('IQR bounds: ', lower, upper);
    const getRoundedInterval = (range) => {
      const unroundedInterval = range / targetValueCount;
      const intervalOfInterval = (10 ** (Math.ceil(Math.log10(unroundedInterval)))) / 2;
      const roundedInterval = roundToNextMultiple(unroundedInterval, intervalOfInterval);
      return roundedInterval;
    }
    const iqrInterval = getRoundedInterval(range);
    // console.log('IQR interval: ' + iqrInterval);

    roundingInterval = iqrInterval;
    const totalUniqueValueCount = new Set(filteredTotalData.map(el => formatValue(el, pinDisplayType.format, true, "-n/a-"))).size;
    const propertyUniqueValueCount = new Set(filteredPropertyData.map(el => formatValue(el, pinDisplayType.format, true, "-n/a-"))).size;
    // console.log('Pin count: ' + propertyData.length)
    // console.log('Total unique value count: ' + totalUniqueValueCount)
    // console.log('Property unique value count: ' + propertyUniqueValueCount)
    const iqrFinalValueArr = [];
      
    return propertyData.map((pin, i) => {
      const propCountFormatted = formatValue(pin.propertiesCount, propertiesCountFieldObj.format, true, '-n/a-');

      const shouldRound = enableRounding && !isAggregateData && pin.isProperty && pin[pinDisplayType.dataField] != null && pinDisplayType.format?.rounding != null && pinDisplayType.format.type !== 'date' && propertyUniqueValueCount > targetValueCount && propertyData.length > minPinCount;
      let value = pin.propertiesCount;
      let didRound = false;
      value = pin[pinDisplayType.dataField];
      let formattedValue = formatValue(pin[pinDisplayType.dataField], pinDisplayType.format, true, "-n/a-");
      if (shouldRound) {
        const roundedValue = roundToNearestMultiple(pin[pinDisplayType.dataField], roundingInterval);
        const iqrRoundedValue = roundToNearestMultiple(pin[pinDisplayType.dataField], iqrInterval);
        const formattedRoundedValue = formatValue(roundedValue, pinDisplayType.format, true, "-n/a-");
        didRound = formattedRoundedValue !== formattedValue;
        if (didRound) {
          value = roundedValue;
        }
        iqrFinalValueArr.push(formatValue(iqrRoundedValue, pinDisplayType.format, true, "-n/a-"));
      }
      
      const tooltip = pin.isProperty
      ? `${pin.clusterName} | ${formatValue(pin[pinDisplayType.dataField], pinDisplayType.format, false, "-n/a-")}`
      : `${propCountFormatted} | ${pin.clusterName}${didRound ? ` | ${formatValue(pin[pinDisplayType.dataField], pinDisplayType.format, false, "-n/a-")}` : ''}`

      // const value = !pinDisplayType
      //   ? pin.propertiesCount
      //   : shouldRound
      //     ? roundToNearestMultiple(pin[pinDisplayType.dataField], interval50)
      //     : pin[pinDisplayType.dataField]

      const text = `${didRound ? '~' : ''}${formatValue(value, pinDisplayType.format, true, "-n/a-")}`;
      const palette = colorPalettePresets.find(palette => palette.id === pinColorPaletteId)
      // const colorArray = propertyUniqueValueCount > targetValueCount ? filteredArray : sortedArray.map(pin => pin[pinDisplayType.dataField]);
      const { lower, upper } = getIQRBounds(filteredTotalData);
      const colorMatrix = getColorForPin(palette, value, lower, upper)

      // console.log('Rounded unique value count (default): ' + new Set(finalValueArr).size)
      // console.log('Rounded unique value count (IQR): ' + new Set(iqrFinalValueArr).size)
      return <PinWithTooltip
        key={objectId + pin.clusterID}
        id={pin.clusterID}
        objectId={objectId}
        onClick={handlePinClick}
        isProperty={pin.isProperty}
        hasPinDisplayType={true}
        pinDisplayType={pinDisplayType}
        hasNonNumericPinDisplayType={pinDisplayType.dataType !== 'number'}
        pinColorIds={pinColorIds}
        lat={pin.latitude}
        lng={pin.longitude}
        tooltip={tooltip}
        value={value}
        text={text}
        valueColor={colorMatrix.valueColor}
        isHighlighted={isHighlighted}
      />;
    });
    
  };
  

}

function mapState({ exploreMap, exploreData, exploreViews }, ownProps) {
  const { markerOptions } = exploreMap;
  const propertyDataObject = exploreData.propertyDataObjects[ownProps.objectId];
  const { pinDisplayType, pinColorIds, pinColorPaletteId, propertyData, isAggregateData } = propertyDataObject;
  return {
    enableRounding: markerOptions.enableRounding,
    isHighlighted: exploreViews.highlightedObjectId === ownProps.objectId,
    pinDisplayType,
    pinColorIds,
    pinColorPaletteId,
    propertyData,
    isAggregateData
  };
}
const mapDispatch = {
  appNotify
};

export default compose(
  connect(mapState, mapDispatch)
)(PropertyDataPins);
